import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import {
  BehaviorSubject,
  EMPTY,
  NEVER,
  Observable,
  ReplaySubject,
  catchError,
  from,
  fromEvent,
  map,
  merge,
  of,
  skipWhile,
  switchMap,
  take,
  tap,
  throwError,
  zip,
} from 'rxjs';
import {
  ClientCard,
  ClientLocationDiscount,
  ClientSubsidy,
  DeleteProfileReq,
  Profile,
  RegisterCompanyCodeReq,
  UpdateProfileReq,
} from '../api/contracts/client';
import { HttpMethods } from '../api/api.enums';
import { environment } from 'src/environments/environment';
import {
  DeliveryLocation,
  DeliveryLocationDTO,
} from '../api/contracts/location';
import { HttpResponse } from '@angular/common/http';
import { Browser } from '@capacitor/browser';
import { PlatformService } from './platform.service';
import { PaymentMethod } from '../types/payment';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private readonly _profile = new ReplaySubject<Profile>(1);
  private readonly _clientLocation = new ReplaySubject<DeliveryLocation>(1);
  private readonly _clientCard = new BehaviorSubject<ClientCard | false | null>(
    null
  );
  private readonly _clientSubsidies = new ReplaySubject<ClientSubsidy[]>(1);

  public readonly profile$: Observable<Profile>;
  public readonly clientLocation$: Observable<DeliveryLocation>;
  public readonly clientCard$: Observable<ClientCard | false | null>;
  public readonly clientSubsidies$: Observable<ClientSubsidy[]>;

  constructor(
    private readonly apiService: ApiService,
    private readonly platformService: PlatformService
  ) {
    this.profile$ = this._profile.asObservable();
    this.clientLocation$ = this._clientLocation.asObservable();
    this.clientCard$ = this._clientCard.asObservable();
    this.clientSubsidies$ = this._clientSubsidies.asObservable();
  }

  init(): Observable<void> {
    return zip(this.retrieveProfile(), this.retrieveClientLocation()).pipe(
      map(() => void 0)
    );
  }

  retrieveProfile(): Observable<Profile> {
    return this.apiService
      .request<Profile>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/client/profile`,
      })
      .pipe(
        map((res) => res.body as Profile),
        tap((res) => this._profile.next(res))
      );
  }

  updateProfile(body: UpdateProfileReq): Observable<Profile> {
    return this.apiService
      .request<Profile, null, UpdateProfileReq>({
        method: HttpMethods.PUT,
        url: `${environment.apiUrl}/client/profile`,
        body,
      })
      .pipe(
        map((res) => res.body as Profile),
        tap((res) => this._profile.next(res))
      );
  }

  deleteProfile(body: DeleteProfileReq): Observable<void> {
    return this.apiService
      .request<void, null, DeleteProfileReq>({
        method: HttpMethods.DELETE,
        url: `${environment.apiUrl}/client/profile_delete`,
        body,
      })
      .pipe(map((res) => res.body as void));
  }

  retrieveClientLocation(): Observable<DeliveryLocation> {
    return this.apiService
      .request<DeliveryLocationDTO>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/client/location`,
      })
      .pipe(
        map((res) => res.body?.location as DeliveryLocation),
        tap((location) => this._clientLocation.next(location))
      );
  }

  retrieveClientCard(): Observable<ClientCard | false> {
    return this.apiService
      .request<ClientCard>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/client/card`,
      })
      .pipe(
        catchError((err) => {
          if (err.status === 404) {
            return of(false);
          }
          return throwError(() => err);
        }),
        map((res) => {
          if (res === false) {
            return false;
          }
          if ((res as HttpResponse<ClientCard>).body?.lastFour === null) {
            return false;
          }
          return (res as HttpResponse<ClientCard>).body as ClientCard;
        }),
        tap((res) => this._clientCard.next(res))
      );
  }

  updateClientLocation(hash: string): Observable<DeliveryLocation> {
    return this.apiService
      .request<DeliveryLocationDTO, null, Pick<DeliveryLocation, 'hash'>>({
        method: HttpMethods.PUT,
        url: `${environment.apiUrl}/client/location`,
        body: {
          hash: hash,
        },
      })
      .pipe(
        map((res) => res.body?.location as DeliveryLocation),
        tap((location) => this._clientLocation.next(location))
      );
  }

  openCardEditingDashboard(
    url: string = environment.dashboardUrl
  ): Observable<void> {
    return from(Browser.open({ url })).pipe(
      switchMap(() =>
        merge(
          fromEvent(window, 'focus'),
          this.platformService.isNativeMobile()
            ? new Observable((obs) => {
                Browser.addListener('browserFinished', () => {
                  obs.next();
                  obs.complete();
                });
              })
            : NEVER
        )
      ),
      take(1),
      map(() => void 0)
    );
  }

  retrieveClientSubsidies(): Observable<ClientSubsidy[]> {
    return this.apiService
      .request<ClientSubsidy[]>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/client/subsidies`,
      })
      .pipe(
        map((res) => res.body as ClientSubsidy[]),
        tap((subsidyDto) => {
          this._clientSubsidies.next(subsidyDto);
        })
      );
  }

  retrieveClientLocationDiscounts(): Observable<ClientLocationDiscount[]> {
    return this.apiService
      .request<ClientLocationDiscount[]>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/client/location_discounts`,
      })
      .pipe(map((res) => res.body as ClientLocationDiscount[]));
  }

  registerCompanyCode(code: string): Observable<void> {
    return this.apiService
      .request<unknown, null, RegisterCompanyCodeReq>({
        method: HttpMethods.PUT,
        url: `${environment.apiUrl}/client/register_company_code`,
        body: {
          code,
        },
      })
      .pipe(
        switchMap((res) => {
          if ((res.body as any)['success'] !== true) {
            return throwError(() => new Error('failed'));
          }
          return of(void 0);
        })
      );
  }
}
